import { takeUntil } from 'rxjs/operators';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output
} from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef
} from '@angular/material/dialog';
import { HireItem } from '../../models/hire-item';
import { Subject } from 'rxjs';
import { AddPaymentDialogComponent } from './add-payment-dialog/add-payment-dialog.component';
import { ConfigService } from '../services/config.service';
import { ConsumableHireItem } from '../../models/consumable-hire-item';
import { Delivery, Payment } from '../../models/index';
import { BookingItem } from '../../models/booking-item';
import { MatButton } from '@angular/material/button';
import { NgIf, PercentPipe, CurrencyPipe } from '@angular/common';
import { FlexModule } from '@angular/flex-layout';

@Component({
    selector: 'app-price-container',
    templateUrl: './price-container.component.html',
    styleUrls: ['./price-container.component.scss'],
    standalone: true,
    imports: [FlexModule, NgIf, MatButton, PercentPipe, CurrencyPipe]
})
export class PriceContainerComponent implements OnChanges, OnDestroy {
  subtotal: number;
  tax: number;
  deliveryTotal: number;
  total: number;
  paid: number;
  balance: number;
  consumables: ConsumableHireItem[] = [];
  private ngUnsubscribe = new Subject<void>();

  private _deliveries: Delivery[] = [];

  @Input() hireItems: HireItem[];

  @Input() bookingItems: BookingItem[] = [];

  @Input() set deliveries(value: Delivery[]) {
    this._deliveries = value;
  }

  @Input() set consumableItemsSubject(value) {
    value.pipe(takeUntil(this.ngUnsubscribe)).subscribe((consumables) => {
      this.consumables = consumables;
      this.calculateTotals();
    });
  }

  @Input() set currencyRate(value: number) {
    this._currencyRate = value;
    this.calculateTotals();
  }

  @Input() paymentableId: number;
  @Input() paymentableType: string;

  @Input() set payments(value: Payment[]) {
    this._payments = value;
    this.calculatePaidAndBalance();
  }

  @Input() taxRate: number;
  @Input() defaultCurrency = 'GBP';
  @Input() paymentableCurrency = 'GBP';

  @Output() paymentAdded = new EventEmitter();
  _payments: Payment[] = [];

  private addPaymentDialogRef: MatDialogRef<AddPaymentDialogComponent>;
  private _currencyRate = 1;

  constructor(public dialog: MatDialog, private configService: ConfigService) {
    this.subtotal = 0;
    this.total = 0;
    this.paid = 0;
  }

  ngOnChanges() {
    this.calculateTotals();
  }

  calculateTotals() {
    this.subtotal = 0;
    this.tax = 0;
    this.deliveryTotal = 0;
    this.calculateItemsPrice();
    this.calculateConsumablesPrice();
    this.calculateDelivery();
    this.calculateTotal();
  }

  private calculateItemsPrice() {
    if (this.paymentableType === 'docket') {
      this.hireItems.forEach((item) => {
        if (item.charge && !item.deleted) {
          this.subtotal += +item.charge * this._currencyRate;
          this.tax += +(
            +item.charge *
            this._currencyRate *
            this.taxRate
          ).toFixed(2);
        }
      });
    } else if (this.paymentableType === 'booking' && this.bookingItems) {
      this.bookingItems.forEach((item) => {
        if (item.charge && !item.deleted_at) {
          this.subtotal += +item.charge * this._currencyRate;
          this.tax += +(+item.charge * this.taxRate).toFixed(2);
        }
      });
    }
  }

  private calculateConsumablesPrice() {
    this.consumables.forEach((consumable) => {
      if (consumable.price) {
        this.subtotal +=
          +consumable.price * consumable.quantity * this._currencyRate;
        this.tax += +(
          +consumable.price *
          consumable.quantity *
          this.taxRate
        ).toFixed(2);
      }
    });
  }

  private calculateDelivery() {
    this._deliveries.forEach((delivery) => {
      if (!delivery.deleted) {
        this.deliveryTotal += delivery.charge * this._currencyRate;
      }
    });
    if (this.deliveryTotal) {
      this.subtotal += this.deliveryTotal;
      this.tax += this.deliveryTotal * this.taxRate;
    }
  }

  private calculateTotal() {
    this.total = this.subtotal + this.tax;
    this.calculatePaidAndBalance();
  }

  viewAddPaymentDialog() {
    let config = {
      data: {
        paymentableId: this.paymentableId,
        paymentableType: this.paymentableType,
        payments: this._payments,
        balance: this.balance,
        defaultCurrency: this.defaultCurrency,
        currency: this.paymentableCurrency,
        currencyRate: this._currencyRate
      }
    } as MatDialogConfig;
    this.addPaymentDialogRef = this.dialog.open(
      AddPaymentDialogComponent,
      config
    );
    this.balance = undefined;
    this.paid = undefined;
    this.addPaymentDialogRef
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((closedPayment) => {
        if (closedPayment) {
          this.configService.currentUser
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((user) => {
              closedPayment.user = user;
            });

          this._payments.push(closedPayment);
        }
        this.paymentAdded.next(closedPayment);
        this.calculatePaidAndBalance();
      });
  }

  calculatePaidAndBalance() {
    this.paid = 0;
    this.balance = this.total;
    this._payments.forEach((payment) => {
      this.paid += payment.amount * this._currencyRate;
      this.balance -= payment.amount * this._currencyRate;
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
